import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { createAppThunk, type IRes } from "store/modules/common";
import Http from "commons/Http";
import { API_NOTICE } from "config/api";
import type {
  INoticeFetc,
  INoticeList,
  INoticeListFect,
  IPageInfo,
  IResNotice,
  IResNoticeList,
  NoticeTabType,
} from "./NoticeVo";
import type { INoticeStatusChannelVo, IResNoticePartnerList } from "components/popup/noticeList/NoticeListVo";

const name = "notice";

export interface INotice {
  activeTab: NoticeTabType;
  noticeText: string;
  title: string;
  list: INoticeList[];
  detail?: INoticeList;
  page_info?: IPageInfo;
  channelList?: { id: number; text: string; value: string }[];
  selectChannelList: { code: string; text: string }[];
  statusChannelList?: INoticeStatusChannelVo[];
}

/**
 * 공지사항 목록 리스트 조회
 */
export const apiGnoticeList = createAppThunk<IResNoticeList, INoticeListFect>(
  `${name}/apiGnoticeList`,
  async (params, thunkApi) => {
    const role = thunkApi.getState().auth.role;
    const cpCode = thunkApi.getState().auth.cp_code;
    // if (role !== Grade.MASTER || cpCode) {
    //   params.code = cpCode;
    // }
    params.size = thunkApi.getState().notice.page_info!.size;
    params.type = "NOTICE";
    params.serviceType = thunkApi.getState().ui.selectbox?.["noticeViewType"]
      ? (thunkApi.getState().ui.selectbox?.["noticeViewType"] as string)
      : "ALL";

    const res = await Http.get<IRes<IResNoticeList>>(`${API_NOTICE}/list`, {
      params,
    });

    if (res.data.content) {
      thunkApi.dispatch(
        rdxSetNoticePageInfo({
          page: res.data.content.data.page_info.page,
          size: 20,
          total_elements: res.data.content.data.page_info.total_elements,
          totalPage: Math.ceil(res.data.content.data.page_info.total_elements! / 20),
        }),
      );

      const pinList = res.data.content.pin_list;
      const list =
        pinList && pinList.length > 0 ? pinList.concat(res.data.content.data.list) : res.data.content.data.list;
      thunkApi.dispatch(rdxSetNoticeList(list));
    }
    return res.data;
  },
);

/**
 * 공지사항 상세 조회
 */
export const apiGnoticeDetail = createAppThunk<INoticeList, number>(
  `${name}/apiGnoticeDetail`,
  async (param, thunkApi) => {
    const res = await Http.get<IRes<INoticeList>>(`${API_NOTICE}/detail/${param}`);

    if (res.data.content) {
      thunkApi.dispatch(rdxSetNoticeDetail(res.data.content));
      thunkApi.dispatch(rdxSetSelectChannelListDetail(res.data.content.code_list ? res.data.content.code_list : []));
      thunkApi.dispatch(rdxSetNoticeTitle(res.data.content.title));
      thunkApi.dispatch(rdxSetNoticeText(res.data.content.content!));
    }
    return res.data;
  },
);

/**
 * 공지사항 등록, 수정
 */
export const apiPnoticeSave = createAppThunk<IResNotice, INoticeFetc>(
  `${name}/apiPnoticeSave`,
  async (params, thunkApi) => {
    params.content = thunkApi.getState().notice.noticeText;
    params.title = thunkApi.getState().notice.title;

    params.type = thunkApi.getState().ui.selectbox?.["noticeTypeSelect"]
      ? (thunkApi.getState().ui.selectbox?.["noticeTypeSelect"] as string)
      : "NOTICE";
    params.service_type = thunkApi.getState().ui.selectbox?.["noticeCpTypeSelect"]
      ? (thunkApi.getState().ui.selectbox?.["noticeCpTypeSelect"] as string)
      : "ALL";
    const codeList = thunkApi.getState().notice.selectChannelList.map(i => i.code);
    params.customer_code_list = codeList;
    params.notice_receiver_type = codeList.length > 0 ? "SELECT" : "ALL";

    const res = await Http.post<IRes<IResNotice>>(`admin/${API_NOTICE}`, params);
    return res.data;
  },
);

/**
 * 공지사항 삭제
 */
export const apiPnoticeDelete = createAppThunk<IResNotice, number>(`${name}/apiPnoticeDelete`, async param => {
  const res = await Http.post(`admin/${API_NOTICE}/delete/${param}`);
  return res.data;
});

/**
 * 공지사항 고정 pin
 */
export const apiPnoticeSetPin = createAppThunk<undefined, number>(
  `${name}/apiPnoticeSetPin`,
  async (param, thunkApi) => {
    const res = await Http.post(`admin/${API_NOTICE}/${param}`, param);
    return res.data;
  },
);

/**
 * 채널 리스트 (공지사항 옵션) - 기존 cp, pp, pn 방식
 */

// export const apiGnoticeChannelList = createAppThunk<
//   {
//     data: {
//       list: Array<{ code: string; name: string; type: string }>;
//     };
//   },
//   undefined
// >(`${name}/apiGnoticeChannelList`, async (_, thunkApi) => {
//   const type = thunkApi.getState().ui.selectbox?.["noticeCpTypeSelect"]
//     ? String(thunkApi.getState().ui.selectbox?.["noticeCpTypeSelect"]).toLocaleLowerCase()
//     : "all";

//   const apiUrl = type !== "all" ? `codebook/provider/${type}` : "codebook/provider";
//   const res = await Http.get<
//     IRes<{
//       data: {
//         list: Array<{ code: string; name: string; type: string }>;
//       };
//     }>
//   >(apiUrl, { params: { filter: true } });
//   try {
//     if (res?.data) {
//       const data = res.data.content?.data.list;
//       const list = data!.map((i, idx) => {
//         return { id: idx, text: i.name, value: i.code };
//       });
//       thunkApi.dispatch(rdxSetChannelList(list));
//     }
//   } catch (err: unknown) {
//     throw new Error(`unCatchError:: ${err}`);
//   }

//   return res.data;
// });

/**
 * 채널 리스트 (공지사항 옵션) - ver2 방식
 */

export const apiGnoticeChannelList = createAppThunk<
  {
    data: {
      list: Array<{ code: string; name: string; type: string }>;
    };
  },
  undefined
>(`${name}/apiGnoticeChannelList`, async (_, thunkApi) => {
  const type = thunkApi.getState().ui.selectbox?.["noticeCpTypeSelect"]
    ? String(thunkApi.getState().ui.selectbox?.["noticeCpTypeSelect"])
    : "ALL";

  const res = await Http.get<
    IRes<{
      data: {
        list: Array<{ code: string; name: string; type: string }>;
      };
    }>
  >("codebook/service", { params: { type: type } });
  try {
    if (res?.data) {
      const data = res.data.content?.data.list;
      const list = data!.map((i, idx) => {
        return { id: idx, text: i.name, value: i.code };
      });
      thunkApi.dispatch(rdxSetChannelList(list));
    }
  } catch (err: unknown) {
    throw new Error(`unCatchError:: ${err}`);
  }

  return res.data;
});

/**
 * 공지사항 보여지는 파트너 리스트 현황
 */
export const apiGnoticePartnerList = createAppThunk<IResNoticePartnerList, number>(
  `${name}/apiGnoticePartnerList`,
  async (param, thunkApi) => {
    const res = await Http.get<IRes<IResNoticePartnerList>>(`admin/${API_NOTICE}/status/${param}`, {
      params: { noticeId: param },
    });

    if (res.data.content) {
      thunkApi.dispatch(rdxSetNoticeStausList(res.data.content.data.list));
    }
    return res.data;
  },
);

const noticeSlice = createSlice({
  name,
  initialState: {
    activeTab: "notice",
    title: "",
    noticeText: "",
    detail: undefined,
    selectChannelList: [],
    list: [],
    page_info: {
      total_elements: 0,
      page: 0,
      size: 20,
      totalPage: 0,
    },
  } as INotice,
  reducers: {
    rdxSetNoticeActiveTab(state: INotice, action: PayloadAction<NoticeTabType>) {
      state.activeTab = action.payload;
    },
    rdxSetNoticeText(state: INotice, action: PayloadAction<string>) {
      state.noticeText = action.payload;
    },
    rdxSetNoticeTitle(state: INotice, action: PayloadAction<string>) {
      state.title = action.payload;
    },
    rdxSetNoticePageInfo(state: INotice, action: PayloadAction<IPageInfo>) {
      state.page_info = action.payload;
    },
    rdxSetNoticeList(state: INotice, action: PayloadAction<INoticeList[]>) {
      state.list = action.payload;
    },
    rdxSetNoticeDetail(state: INotice, action: PayloadAction<INoticeList>) {
      state.detail = action.payload;
    },
    rdxResetNoticeDetail(state: INotice) {
      delete state.detail;
      delete state.channelList;
      state.selectChannelList = [];
      state.title = "";
      state.noticeText = "";
    },
    rdxResetNoticePage(state: INotice) {
      state.activeTab = "notice";
      state.list = [];
      state.page_info = {
        page: 0,
        size: 20,
        total_elements: 0,
      };
    },
    rdxSetChannelList(state: INotice, action: PayloadAction<{ id: number; text: string; value: string }[]>) {
      state.channelList! = action.payload;
    },

    rdxSetNoticeStausList(state: INotice, action: PayloadAction<INoticeStatusChannelVo[]>) {
      state.statusChannelList = action.payload;
    },

    rdxResetNoticeStausList(state: INotice) {
      delete state.statusChannelList;
    },

    rdxSetSelectChannelList(state: INotice, action: PayloadAction<{ code: string; text: string }>) {
      const list = state.selectChannelList.map(i => i.code);
      if (!list.includes(action.payload.code)) {
        state.selectChannelList = state.selectChannelList.concat(action.payload);
      }
    },
    rdxSetSelectChannelListDetail(state: INotice, action: PayloadAction<{ code: string; name: string }[]>) {
      const list = action.payload.map(i => {
        return { code: i.code, text: i.name };
      });
      state.selectChannelList = list;
    },
    rdxResetSelectChannelList(state: INotice, action: PayloadAction<string>) {
      const list = state.selectChannelList;
      const removeTarget = list!.filter(item => {
        return item.code !== action.payload;
      });
      state.selectChannelList = removeTarget;
    },
  },
});

export const {
  rdxSetNoticeActiveTab,

  rdxSetNoticeText,
  rdxSetNoticeTitle,
  rdxSetNoticeList,
  rdxSetNoticeDetail,
  rdxResetNoticeDetail,
  rdxSetNoticePageInfo,
  rdxResetNoticePage,
  rdxSetChannelList,

  rdxSetSelectChannelList,
  rdxSetSelectChannelListDetail,
  rdxResetSelectChannelList,

  rdxSetNoticeStausList,
  rdxResetNoticeStausList,
} = noticeSlice.actions;
// data 를 관리하는 reducer 기본 반환 설정
export default noticeSlice.reducer;
